import { FileDown, Printer, Utensils, CalendarDays } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Mobile-only Daily Consumption layout. Visible <md (768px). The wide
// desktop ledger table doesn't shrink well on phones — we present a
// per-day card list with the essentials (meals served, day cost, top 4
// ingredient lines) instead. Power users can flip to desktop for the
// full 14-column ledger.

const QTY_ONLY = new Set(["Rice"]);

function fmtQty(it, qty, unit = "kg") {
  if (it === "Egg") return `${Math.round(qty)} pcs`;
  const v = ["gm", "ml"].includes(unit) ? qty / 1000 : qty;
  return `${(Number(v) || 0).toFixed(3)} ${["gm", "ml"].includes(unit) ? (unit === "gm" ? "kg" : "L") : unit}`;
}

const money = (n) => `Rs ${(Number(n) || 0).toFixed(2)}`;

export default function MobileDailyConsumption(props) {
  const {
    active, year, month, classKey, setYear, setMonth, setClassKey,
    yearChoices, MONTHS, CLASS_OPTIONS,
    rows, totals, activeItems, monthLabel, classLabel,
    openingRice, stockSeries,
    downloadCsv, doPrint,
  } = props;

  const totalCons = stockSeries.reduce((s, x) => s + x.used, 0);
  const closingRice = stockSeries.length ? stockSeries[stockSeries.length - 1].closing : openingRice;

  return (
    <div className="md:hidden" data-testid="mobile-daily-consumption">
      {/* Hero */}
      <header className="px-5 pt-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-clay">Reports</p>
        <h1 className="mt-2 flex items-center gap-2 font-heading text-4xl font-bold text-ink-900">
          <Utensils className="h-7 w-7 text-clay" /> Daily Consumption
        </h1>
        <p className="mt-2 text-sm text-ink-700">
          <b className="text-ink-900">{active?.schoolName || "—"}</b>
          <span className="mx-1.5 text-ink-500">•</span>
          UDISE: {active?.udiseCode || "—"}
        </p>
      </header>

      {/* Filters */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-dc-filters">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="m-dc-year" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="m-dc-month" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </label>
        </div>
        <label className="mt-3 block">
          <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Class Category</span>
          <Select value={classKey} onValueChange={setClassKey}>
            <SelectTrigger data-testid="m-dc-class" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{CLASS_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <p className="mt-3 text-[0.7rem] text-ink-700">
          Showing <b>{classLabel}</b> · {monthLabel} {year} · <b>{rows.length}</b> day{rows.length === 1 ? "" : "s"} of saved entries.
        </p>
      </section>

      {/* Month summary */}
      <section className="mx-4 mt-4 grid grid-cols-2 gap-3" data-testid="m-dc-summary">
        <div className="rounded-2xl border border-border bg-white p-3.5 shadow-[0_1px_0_rgba(44,53,40,0.04)]">
          <p className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Total Meals</p>
          <p className="mt-1 font-heading text-2xl font-bold text-ink-900">{totals.mealsServed}</p>
        </div>
        <div className="rounded-2xl border border-border bg-white p-3.5 shadow-[0_1px_0_rgba(44,53,40,0.04)]">
          <p className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Total Cost</p>
          <p className="mt-1 font-heading text-2xl font-bold text-clay">{money(totals.dayCost)}</p>
        </div>
        <div className="col-span-2 rounded-2xl border border-border bg-sand-50 p-3.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Rice Stock</p>
            <span className="text-[0.65rem] font-semibold text-ink-700">opening → closing</span>
          </div>
          <p className="mt-1 font-mono text-sm font-semibold text-ink-900">
            {(Number(openingRice) || 0).toFixed(3)} kg
            <span className="mx-1.5 text-clay">−</span>
            <span className="text-destructive">{(Number(totalCons) || 0).toFixed(3)} kg</span>
            <span className="mx-1.5 text-clay">=</span>
            <b className={closingRice < 0 ? "text-destructive" : "text-clay"}>{(Number(closingRice) || 0).toFixed(3)} kg</b>
          </p>
        </div>
      </section>

      {/* Day cards */}
      <section className="mx-4 mt-4 space-y-3" data-testid="m-dc-day-cards">
        {rows.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border bg-white/50 p-6 text-center">
            <CalendarDays className="mx-auto h-8 w-8 text-ink-500" />
            <p className="mt-2 text-sm text-ink-700">No saved daily entries for {monthLabel} {year}.</p>
          </div>
        ) : rows.map((r) => {
          // Top 4 ingredient lines by amount
          const ingredients = activeItems
            .map((it) => ({ it, ...(r.items.get(it) || { qty: 0, amount: 0, unit: "kg" }) }))
            .sort((a, b) => (b.amount || 0) - (a.amount || 0))
            .slice(0, 4)
            .filter((x) => (x.qty || 0) > 0 || (x.amount || 0) > 0);
          return (
            <div key={r.date} className="rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid={`m-dc-row-${r.date}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading text-base font-bold text-ink-900">{r.date.slice(8, 10)} · {r.day}</p>
                  <p className="truncate text-[0.7rem] text-ink-700">{r.mealName}</p>
                </div>
                <p className="font-heading text-sm font-bold text-clay">{money(r.dayCost)}</p>
              </div>
              <div className="mt-2 flex items-center gap-3 rounded-xl bg-sand-50 px-3 py-1.5 text-[0.7rem]">
                <span><b className="text-ink-900">{r.mealsServed}</b> meals</span>
                {r.ladooPieces > 0 && <span><b className="text-ink-900">{Math.round(r.ladooPieces)}</b> ladoos</span>}
              </div>
              {ingredients.length > 0 && (
                <ul className="mt-3 space-y-1.5 text-[0.75rem]">
                  {ingredients.map((x) => (
                    <li key={x.it} className="flex items-center justify-between gap-2">
                      <span className="text-ink-900">{x.it}</span>
                      <span className="font-mono text-ink-700">
                        {fmtQty(x.it, x.qty, x.unit)}
                        {!QTY_ONLY.has(x.it) && <span className="ml-2 text-clay">{money(x.amount)}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </section>

      {/* Export actions */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-dc-exports">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Download / Print</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={downloadCsv} data-testid="m-dc-csv" className="flex items-center justify-center gap-2 rounded-xl border border-border bg-white py-3 text-xs font-semibold text-ink-900 active:bg-sand-50">
            <FileDown className="h-4 w-4 text-clay" /> CSV
          </button>
          <button onClick={doPrint} data-testid="m-dc-print" className="flex items-center justify-center gap-2 rounded-xl border border-border bg-white py-3 text-xs font-semibold text-ink-900 active:bg-sand-50">
            <Printer className="h-4 w-4 text-clay" /> Print
          </button>
        </div>
      </section>

      <div className="h-24" aria-hidden />
    </div>
  );
}
